// Shared cleanup for a freshly-generated AI document reply, extracted from
// ConsultationChat.tsx/DischargeSummaryChat.tsx's near-identical local
// copies. The model often wraps the actual document in conversational
// filler despite the system prompt asking it not to — a leading "Certainly!
// Let's..." line, the "--- ... ---" divider marker itself, and a trailing
// "Please review..." sign-off. None of that belongs in what gets reviewed/
// saved as the real document, so strip it before it ever reaches the
// approve box.

// Drop a leading line that just echoes the document's own title (e.g. the
// model writing "Consultation Notes for Ram Kumar" as its first line, or
// wrapping it as a "**Consultation Notes**" heading) — redundant since the
// generated .docx already carries doc_type as its own title line (see
// docxGenerator.js's textToDocxBuffer), so this doubled it. The `^`/`$`
// anchors mean this only ever matches a line that is ENTIRELY the title
// (plus an optional "for X"/":..." suffix) — a line that merely mentions
// "Consultation Notes" mid-sentence is untouched. Markdown wrapping
// (`**bold**`, `*italic*`, a leading "#" heading marker) is stripped before
// testing, not baked into the regex, so it matches however the title was
// formatted that time.
//
// Exported on its own (not just inlined in cleanAIDraft below) so a
// manually-authored document — which deliberately skips the rest of this
// file's AI-tuned cleanup, since a doctor's own wording shouldn't be
// preamble-stripped or sign-off-trimmed — can still get this one, narrow
// fix: ConsultationChat.tsx's useManualDraft() calls it directly.
export function stripTitleEcho(text: string, lines?: string[]): string[] {
  const arr = lines ?? text.split('\n');
  const TITLE_ECHO_RE = /^(OPD Prescription|Prescription(?:\s+Sheet)?|Discharge Summary|Consultation Notes)\s*(for\s+.+|[:\-–—].*)?$/i;
  while (arr.length) {
    const bare = arr[0]!.trim().replace(/^#{1,6}\s+/, '').replace(/^\*{1,2}(.+?)\*{1,2}$/, '$1').trim();
    if (!TITLE_ECHO_RE.test(bare)) break;
    arr.shift();
  }
  return arr;
}

const DRAFT_MARKER_RE = /^-{2,}.*AI-ASSISTED DRAFT.*-{2,}\s*$/i;

// Marks a doctor-authored (no-AI) draft in the persisted transcript,
// mirroring the "--- AI-ASSISTED DRAFT ---" marker so a page reload can
// tell the two apart (and skip running the AI-tuned cleanup on manual text,
// which would mangle content that doesn't follow the AI's own conventions).
// Shared with the v3 conversational layout's ConsultationTranscript.tsx,
// which needs the same distinction to render a transcript bubble correctly.
export const MANUAL_MARKER = '--- MANUAL DRAFT (Doctor authored) ---';

// The system prompt asks the model to prefix a finished document with the
// exact "--- AI-ASSISTED DRAFT ---" line, but it doesn't always comply —
// especially on a follow-up turn ("Let's update the notes with these
// details...") where the model treats the reply as a conversational update
// rather than a fresh "final document" moment. When that marker is missing,
// recognizing the SOAP section labels themselves is a second, independent
// way to catch "this reply is a document" that doesn't depend on the model
// remembering one exact string.
export function looksLikeDocument(text: string): boolean {
  if (text.includes('AI-ASSISTED DRAFT') || text.includes('Rx.') || text.includes('DISCHARGE')) return true;
  const sectionHits = ['subjective', 'assessment', 'plan'].filter(k => new RegExp(`\\*{0,2}${k}\\*{0,2}:?`, 'i').test(text)).length;
  return sectionHits >= 2;
}

/**
 * Splits a reply into what the AI said to the doctor and the document it
 * drafted. The chat part is everything before the "--- AI-ASSISTED DRAFT ---"
 * marker line; the document is everything after it, cleaned. With no marker,
 * `doc` is null and the whole reply is chat.
 */
export function splitDraftReply(text: string): { chat: string; doc: string | null } {
  const lines = (text || '').split('\n');
  const markerAt = lines.findIndex(l => DRAFT_MARKER_RE.test(l.trim()));
  if (markerAt === -1) return { chat: text, doc: null };
  return {
    chat: lines.slice(0, markerAt).join('\n').trim(),
    doc: cleanAIDraft(lines.slice(markerAt + 1).join('\n')),
  };
}

export function cleanAIDraft(text: string): string {
  if (!text) return text;
  let lines = text.split('\n');

  // Drop the "--- ... ---" divider marker line entirely.
  lines = lines.filter(l => !/^-{2,}.*-{2,}\s*$/.test(l.trim()));

  // Drop leading conversational preamble — everything before the first line
  // that actually looks like document content (a bold heading, a markdown
  // heading, or an Rx line).
  const firstContentIdx = lines.findIndex(l =>
    /^\*\*[^*]+\*\*/.test(l.trim()) || /^#{1,6}\s+\S/.test(l.trim()) || /^Rx\./.test(l.trim())
  );
  if (firstContentIdx > 0) lines = lines.slice(firstContentIdx);

  lines = stripTitleEcho(text, lines);

  // Drop trailing conversational sign-off lines (e.g. "Please review and
  // confirm if any modifications are needed.", "Let me know if...").
  const signOffPattern = /^(please (review|let)|let me know|feel free to|does this|is there anything else)/i;
  while (lines.length && signOffPattern.test(lines[lines.length - 1]!.trim())) lines.pop();

  // Trim stray leading/trailing blank lines left behind by the above.
  while (lines.length && !lines[0]!.trim()) lines.shift();
  while (lines.length && !lines[lines.length - 1]!.trim()) lines.pop();

  let cleaned = lines.join('\n');

  // The model sometimes leaves the literal placeholder instead of filling in
  // today's date — replace it with the real current date.
  const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  cleaned = cleaned.replace(/\[today'?s date\]/gi, today);

  return cleaned;
}
